/**
 * Shared right-click menu over blocks — one item definition for every
 * surface (outline, canvas, document). The controller is created once per
 * builder shell (Builder.vue passes its dependencies explicitly — the
 * provide/inject pair only serves descendants) and entry points call
 * open() with viewport coordinates; BuilderContextMenu.vue renders the
 * result through UiContextMenu. Item building is a pure function
 * (buildBlockMenuModel) so Vitest covers the enable/visibility matrix
 * without a component tree.
 */

import { computed, inject, provide, ref } from 'vue';
import type { ComputedRef, InjectionKey, Ref } from 'vue';
import type { ContextMenuItem } from '@src/Common/Components/UI/ContextMenu/ContextMenu.vue';
import { blockContextOf } from './blockContext';
import type { BlockContext } from './blockContext';
import { readPasteable, writeClipboard } from './clipboard';
import { canPlace } from './nestingRules';
import { insertColumn, insertRow, normalizeMatrix, removeColumn, removeRow } from './tableMatrix';
import type { BlockNode } from './types';
import type { BuilderHost, BuilderTranslator } from './host';
import type { BuilderStore } from './useBuilderStore';
import type { BuilderBridge } from './useBuilderBridge';
import type { BuilderRegistry } from './useBuilderRegistry';
import type { CanvasChrome } from './useCanvasChrome';
import { discardDraft, publishDraft } from './useDraftAutosave';
import type { DraftAutosave } from './useDraftAutosave';
import type { PaletteHistory } from './usePaletteHistory';
import type { PanelManager } from './usePanelManager';
import type { SurfaceController } from './Document/surfaceMode';

export type BlockMenuSurface = 'outline' | 'canvas' | 'document';

export interface BlockMenuOpenOptions {
    /** Surface the menu was invoked from — steers scroll-to/settings routing. */
    surface: BlockMenuSurface;
    /** Document-surface settings opener (the popover lives in DocBlockList). */
    openSettings?: () => void;
    /** Table cell under the pointer — adds the row/column section. */
    tableCell?: { attr: string; row: number; col: number } | null;
}

/** What the menu points at: one block, or the page/document as a whole. */
export type BlockMenuTarget =
    | { kind: 'block'; blockId: string; options: BlockMenuOpenOptions }
    | { kind: 'page' };

export interface BlockContextMenuController {
    state: Ref<{ x: number; y: number; target: BlockMenuTarget } | null>;
    items: ComputedRef<ContextMenuItem[]>;
    open(position: { x: number; y: number }, blockId: string, options: BlockMenuOpenOptions): void;
    /** Page-level menu (surface background, builder bar) — no selection change. */
    openPage(position: { x: number; y: number }): void;
    close(): void;
}

export interface BlockMenuDependencies {
    store: BuilderStore;
    bridge: BuilderBridge;
    registry: BuilderRegistry;
    history: PaletteHistory;
    surface: SurfaceController | null;
    t: BuilderTranslator;
    /** Page-menu seams — absent in harnesses that only need block menus. */
    panels?: PanelManager | null;
    autosave?: DraftAutosave | null;
    chrome?: CanvasChrome | null;
    host?: BuilderHost | null;
    hasSettings?: () => boolean;
    /** Dialog-backed discard confirm; absent = window.confirm fallback. */
    confirmDiscard?: () => Promise<boolean>;
}

interface BlockMenuModelInput {
    t: BuilderTranslator;
    readonly: boolean;
    context: BlockContext | null;
    /** Placeable types for the insert submenu (already filtered + capped). */
    insertTypes: { slug: string; name: string }[];
    surface: BlockMenuSurface;
    /** Whether the clipboard holds blocks placeable at this target. */
    canPaste: boolean;
    /** Table cell context; null outside tables. */
    tableCell: { row: number; col: number } | null;
    actions: {
        tableOp(op: TableOp): void;
        duplicate(): void;
        remove(): void;
        moveUp(): void;
        moveDown(): void;
        insertBefore(slug: string): void;
        insertAfter(slug: string): void;
        copy(): void;
        pasteBefore(): void;
        pasteAfter(): void;
        openSettings(): void;
        scrollToBlock(): void;
    };
}

export interface PageMenuModelInput {
    t: BuilderTranslator;
    readonly: boolean;
    hasSettings: boolean;
    /** Panel manager present — outline/settings items need somewhere to open. */
    hasPanels: boolean;
    surface: 'canvas' | 'document';
    /** Host offers both surfaces — shows the switch item. */
    surfaceAvailable: boolean;
    /** Draft-backed shell — publish/discard/new-window/copy-link gate. */
    hasAutosave: boolean;
    /** Discard dims when the draft matches the published content. */
    draftChanged: boolean;
    canUndo: boolean;
    canRedo: boolean;
    insertTypes: { slug: string; name: string }[];
    actions: {
        openSettings(): void;
        toggleOutline(): void;
        undo(): void;
        redo(): void;
        openWindow(): void;
        toggleSurface(): void;
        publish(): void;
        discard(): void;
        insertAtEnd(slug: string): void;
        copyLink(): void;
    };
}

const INSERT_CAP = 12;

type TableOp = 'row_before' | 'row_after' | 'row_delete' | 'col_before' | 'col_after' | 'col_delete';

const TABLE_OPS: Record<TableOp, (matrix: string[][], cell: { row: number; col: number }) => string[][]> = {
    row_before: (matrix, cell) => insertRow(matrix, cell.row),
    row_after: (matrix, cell) => insertRow(matrix, cell.row + 1),
    row_delete: (matrix, cell) => removeRow(matrix, cell.row),
    col_before: (matrix, cell) => insertColumn(matrix, cell.col),
    col_after: (matrix, cell) => insertColumn(matrix, cell.col + 1),
    col_delete: (matrix, cell) => removeColumn(matrix, cell.col),
};

const BLOCK_MENU_KEY: InjectionKey<BlockContextMenuController> = Symbol('builder-v2-block-menu');

/**
 * Pure item builder — the whole visibility/enable matrix in one place.
 *
 * @param input Menu model input.
 * @return Items for UiContextMenu.
 */
export function buildBlockMenuModel(input: BlockMenuModelInput): ContextMenuItem[] {
    const { t, context, actions } = input;
    const items: ContextMenuItem[] = [];
    const typeChildren = (pick: (slug: string) => void): ContextMenuItem[] =>
        input.insertTypes.map((type) => ({ label: type.name, action: () => pick(type.slug) }));

    if (!input.readonly && input.tableCell !== null) {
        items.push(
            { label: t('table_add_row_before'), icon: 'phosphor/regular/rows', action: () => actions.tableOp('row_before') },
            { label: t('table_add_row_after'), icon: 'phosphor/regular/rows', action: () => actions.tableOp('row_after') },
            { label: t('table_delete_row'), icon: 'phosphor/regular/minus', action: () => actions.tableOp('row_delete') },
            {
                label: t('table_add_column_before'),
                icon: 'phosphor/regular/columns',
                action: () => actions.tableOp('col_before'),
            },
            {
                label: t('table_add_column_after'),
                icon: 'phosphor/regular/columns',
                action: () => actions.tableOp('col_after'),
            },
            { label: t('table_delete_column'), icon: 'phosphor/regular/minus', action: () => actions.tableOp('col_delete') },
        );
    }

    if (!input.readonly) {
        items.push(
            { label: t('duplicate'), icon: 'phosphor/regular/copy', action: actions.duplicate },
            { label: t('copy'), icon: 'phosphor/regular/clipboard', action: actions.copy },
            {
                label: t('paste_before'),
                icon: 'phosphor/regular/clipboard-text',
                disabled: !input.canPaste,
                action: actions.pasteBefore,
            },
            {
                label: t('paste_after'),
                icon: 'phosphor/regular/clipboard-text',
                disabled: !input.canPaste,
                action: actions.pasteAfter,
            },
            {
                label: t('move_up'),
                icon: 'phosphor/regular/arrow-up',
                disabled: context === null || context.index === 0,
                action: actions.moveUp,
            },
            {
                label: t('move_down'),
                icon: 'phosphor/regular/arrow-down',
                disabled: context === null || context.index >= context.count - 1,
                action: actions.moveDown,
            },
            {
                label: t('insert_before'),
                icon: 'phosphor/regular/arrow-line-up',
                disabled: input.insertTypes.length === 0,
                children: typeChildren(actions.insertBefore),
            },
            {
                label: t('insert_after'),
                icon: 'phosphor/regular/arrow-line-down',
                disabled: input.insertTypes.length === 0,
                children: typeChildren(actions.insertAfter),
            },
        );
    }

    items.push({ label: t('open_settings'), icon: 'phosphor/regular/gear-six', action: actions.openSettings });

    if (input.surface === 'outline') {
        items.push({
            label: t('scroll_to_block'),
            icon: 'phosphor/regular/crosshair-simple',
            action: actions.scrollToBlock,
        });
    }

    if (!input.readonly) {
        items.push({ label: t('delete'), icon: 'phosphor/regular/trash', danger: true, action: actions.remove });
    }

    return items;
}

/**
 * Pure item builder for the page-level menu (surface background, builder
 * bar) — whole-document actions only, no block target.
 *
 * @param input Menu model input.
 * @return Items for UiContextMenu.
 */
export function buildPageMenuModel(input: PageMenuModelInput): ContextMenuItem[] {
    const { t, actions } = input;
    const items: ContextMenuItem[] = [];

    if (input.hasSettings && input.hasPanels) {
        items.push({ label: t('page_settings'), icon: 'phosphor/regular/gear', action: actions.openSettings });
    }

    if (input.hasPanels) {
        items.push({ label: t('outline'), icon: 'phosphor/regular/tree-structure', action: actions.toggleOutline });
    }

    if (!input.readonly) {
        items.push(
            {
                label: t('undo'),
                icon: 'phosphor/regular/arrow-counter-clockwise',
                disabled: !input.canUndo,
                action: actions.undo,
            },
            {
                label: t('redo'),
                icon: 'phosphor/regular/arrow-clockwise',
                disabled: !input.canRedo,
                action: actions.redo,
            },
        );
    }

    if (input.hasAutosave) {
        items.push({
            label: t('open_in_new_window'),
            icon: 'phosphor/regular/arrow-square-out',
            action: actions.openWindow,
        });
        items.push({ label: t('page_menu_copy_link'), icon: 'phosphor/regular/link', action: actions.copyLink });
    }

    if (input.surfaceAvailable) {
        items.push({
            label: input.surface === 'document' ? t('page_menu_switch_to_canvas') : t('page_menu_switch_to_document'),
            icon: input.surface === 'document' ? 'phosphor/regular/layout' : 'phosphor/regular/article',
            action: actions.toggleSurface,
        });
    }

    if (!input.readonly) {
        items.push({
            label: t('page_menu_insert_end'),
            icon: 'phosphor/regular/plus',
            disabled: input.insertTypes.length === 0,
            children: input.insertTypes.map((type) => ({
                label: type.name,
                action: () => actions.insertAtEnd(type.slug),
            })),
        });
    }

    if (input.hasAutosave && !input.readonly) {
        items.push({
            label: t('publish_changes'),
            icon: 'phosphor/regular/paper-plane-tilt',
            action: actions.publish,
        });
        items.push({
            label: t('discard'),
            icon: 'phosphor/regular/clock-counter-clockwise',
            danger: true,
            disabled: !input.draftChanged,
            action: actions.discard,
        });
    }

    return items;
}

/** Recents-first placeable types for the insert submenu (BlockInserter order). */
function placeableTypes(deps: BlockMenuDependencies, parent: BlockNode | null): { slug: string; name: string }[] {
    const probe = (slug: string): BlockNode => ({ id: 'pb2-probe', type: slug, useBlock: false, value: {}, settings: {} });
    const types = deps.registry.types.value.filter((type) => canPlace(probe(type.slug), parent));
    const recentSlugs = deps.history.recent.value.filter((entry) => entry.kind === 'type').map((entry) => entry.key);
    const recent = recentSlugs
        .map((slug) => types.find((type) => type.slug === slug))
        .filter((type): type is (typeof types)[number] => type !== undefined);
    const rest = types.filter((type) => !recentSlugs.includes(type.slug));

    return [...recent, ...rest].slice(0, INSERT_CAP).map((type) => ({ slug: type.slug, name: type.name }));
}

export function createBlockContextMenu(deps: BlockMenuDependencies): BlockContextMenuController {
    const state = ref<BlockContextMenuController['state']['value']>(null);

    const newTypeBlock = (slug: string): BlockNode => ({
        type: slug,
        useBlock: false,
        value: deps.registry.defaultValueFor(slug),
        settings: {},
    });

    // Actions never read state.value — the teleported submenu counts as a
    // click "outside" the root menu, so close() (state = null) can run
    // before the clicked action does. Everything is captured when the
    // items are built instead.
    const insertType = (slug: string, context: BlockContext, beforeId: string | null): void => {
        if (deps.store.insertAt(newTypeBlock(slug), context.parentId, context.slot, beforeId) !== null) {
            deps.history.pushRecent({ kind: 'type', key: slug });
        }
    };

    const openSettings = (blockId: string, options: BlockMenuOpenOptions): void => {
        if (options.openSettings) {
            options.openSettings();
            return;
        }

        deps.store.select(blockId);
        deps.store.panelFocusSerial.value += 1;
    };

    const scrollToBlock = (blockId: string): void => {
        if (deps.surface?.surface.value === 'document') {
            document
                .querySelector(`[data-pb2-doc-block="${blockId}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        deps.bridge.scrollTo(blockId);
    };

    const pasteable = (parent: BlockNode | null): BlockNode[] | null =>
        readPasteable(parent, (slug) => deps.registry.typeOf(slug) !== null);

    const paste = (context: BlockContext, beforeId: string | null): void => {
        const parent = context.parentId === null ? null : deps.store.findBlock(context.parentId);
        const blocks = pasteable(parent);

        if (blocks !== null) {
            deps.store.pasteAt(blocks, context.parentId, context.slot, beforeId);
        }
    };

    /** Preview URL for open-in-new-window / copy-link from the page menu. */
    const pageUrl = (): string | null =>
        deps.chrome?.previewUrl.value ?? deps.host?.preview.previewUrl(0) ?? null;

    /** Canvas surface refreshes the preview after publish/discard. */
    const reloadAfter = (): (() => void) | null =>
        deps.surface?.surface.value === 'document' ? null : (deps.chrome?.reload ?? null);

    const pageItems = (): ContextMenuItem[] =>
        buildPageMenuModel({
            t: deps.t,
            readonly: deps.store.mode.value === 'readonly',
            hasSettings: deps.hasSettings?.() ?? false,
            hasPanels: Boolean(deps.panels),
            surface: deps.surface?.surface.value === 'document' ? 'document' : 'canvas',
            surfaceAvailable: deps.surface?.available ?? false,
            hasAutosave: Boolean(deps.autosave),
            draftChanged: deps.autosave?.changed.value ?? true,
            canUndo: deps.store.canUndo.value,
            canRedo: deps.store.canRedo.value,
            insertTypes: placeableTypes(deps, null),
            actions: {
                openSettings: () => deps.panels?.open('page'),
                toggleOutline: () => deps.panels?.toggle('outline'),
                undo: () => deps.store.undo(),
                redo: () => deps.store.redo(),
                openWindow: () => {
                    const url = pageUrl();

                    if (url !== null) {
                        window.open(url, '_blank', 'noopener');
                    }
                },
                toggleSurface: () =>
                    deps.surface?.set(deps.surface.surface.value === 'document' ? 'canvas' : 'document'),
                publish: () => {
                    if (deps.autosave) {
                        void publishDraft(deps.autosave, reloadAfter());
                    }
                },
                discard: () => {
                    if (deps.autosave && deps.autosave.changed.value) {
                        const confirm =
                            deps.confirmDiscard ??
                            ((): Promise<boolean> => Promise.resolve(window.confirm(deps.t('discard_confirm'))));

                        void discardDraft(deps.autosave, confirm, reloadAfter());
                    }
                },
                insertAtEnd: (slug) => {
                    const context: BlockContext = {
                        parentId: null,
                        slot: 0,
                        index: deps.store.content.value.length,
                        count: deps.store.content.value.length,
                        beforeId: null,
                        nextId: null,
                    };
                    insertType(slug, context, null);
                },
                copyLink: () => {
                    const url = pageUrl();

                    if (url !== null) {
                        // Insecure contexts (and jsdom) have no clipboard API.
                        void navigator.clipboard?.writeText(new URL(url, window.location.origin).href);
                    }
                },
            },
        });

    const items = computed<ContextMenuItem[]>(() => {
        const current = state.value;

        if (current === null) {
            return [];
        }

        if (current.target.kind === 'page') {
            return pageItems();
        }

        const { blockId: id, options } = current.target;
        const context = blockContextOf(deps.store.content.value, id);
        const parent = context?.parentId ? deps.store.findBlock(context.parentId) : null;

        return buildBlockMenuModel({
            t: deps.t,
            readonly: deps.store.mode.value === 'readonly',
            context,
            insertTypes: placeableTypes(deps, parent),
            surface: options.surface,
            canPaste: pasteable(parent) !== null,
            tableCell: options.tableCell ?? null,
            actions: {
                tableOp: (op) => {
                    const cell = options.tableCell;
                    const block = deps.store.findBlock(id);

                    if (!cell || block === null) {
                        return;
                    }

                    const matrix = normalizeMatrix((block.value ?? {})[cell.attr]);
                    const next = TABLE_OPS[op](matrix, cell);

                    if (next !== matrix) {
                        // Default origin on purpose: the canvas patch loop must
                        // re-render the structurally changed table.
                        deps.store.updateValue(id, cell.attr, next);
                    }
                },
                duplicate: () => deps.store.duplicateBlock(id),
                remove: () => deps.store.removeBlock(id),
                moveUp: () => deps.store.moveBlock(id, -1),
                moveDown: () => deps.store.moveBlock(id, 1),
                insertBefore: (slug) => context && insertType(slug, context, context.beforeId),
                insertAfter: (slug) => context && insertType(slug, context, context.nextId),
                copy: () => {
                    const block = deps.store.findBlock(id);

                    if (block !== null) {
                        writeClipboard([block]);
                    }
                },
                pasteBefore: () => context && paste(context, context.beforeId),
                pasteAfter: () => context && paste(context, context.nextId),
                openSettings: () => openSettings(id, options),
                scrollToBlock: () => scrollToBlock(id),
            },
        });
    });

    const controller: BlockContextMenuController = {
        state,
        items,
        open(position, blockId, options) {
            if (!deps.store.selectedIds.value.includes(blockId)) {
                deps.store.select(blockId);
            }

            state.value = { x: position.x, y: position.y, target: { kind: 'block', blockId, options } };
        },
        openPage(position) {
            state.value = { x: position.x, y: position.y, target: { kind: 'page' } };
        },
        close() {
            state.value = null;
        },
    };

    provide(BLOCK_MENU_KEY, controller);

    return controller;
}

export function useBlockContextMenuOptional(): BlockContextMenuController | null {
    return inject(BLOCK_MENU_KEY, null);
}
